import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star } from 'lucide-react';
import StarCard from './StarCard';
import { peopleApi, languageToTMDB } from '../services/api';
import { useLanguage } from '../context/LanguageContext';

const StarsSection = () => {
  const { t, language } = useLanguage();
  const [stars, setStars] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStars = async () => {
      try {
        setLoading(true);
        const tmdbLang = languageToTMDB[language] || 'en-US';
        const data = await peopleApi.getPopular(1, tmdbLang);
        // Get first 6 stars for homepage
        // Filter to only show stars with profile photos
        const starsWithPhotos = data.results.filter(star => star.profile_path);
        setStars(starsWithPhotos.slice(0, 6));
      } catch (error) {
        console.error('Error fetching stars:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStars();
  }, [language]);

  if (loading) {
    return (
      <section className="bg-[#0B0F0E] py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-[7.6923%]">
          <div className="animate-pulse">
            <div className="h-10 bg-[#1a2420] rounded w-64 mb-8" />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="aspect-[2/3] bg-[#1a2420] rounded-lg" />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-[#0B0F0E] py-24">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-[7.6923%]">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-12">
          <div className="flex items-center gap-4">
            <Star className="w-8 h-8 text-[#52ea88]" />
            <div>
              <h2 className="text-3xl md:text-4xl font-semibold text-[#E6FFF6]">
                {t('stars.title') || 'Hollywood Stars'}
              </h2>
              <p className="text-[#9FBFB4] mt-1">
                {t('stars.subtitle') || 'Discover the talent behind the magic'}
              </p>
            </div>
          </div>
          
          <Link
            to="/stars"
            className="group flex items-center gap-2 text-[#52ea88] hover:text-[#E6FFF6] transition-colors"
          >
            <span>{t('stars.viewAll') || 'View All'}</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* Stars Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
          {stars.map((star) => (
            <StarCard key={star.id} star={star} compact />
          ))}
        </div>
      </div>
    </section>
  );
};

export default StarsSection;
